import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ref, onValue, set, get } from 'firebase/database';
import { LogIn } from 'lucide-react';

// ─── Canvas ───────────────────────────────────────────────────────────────────
export const CANVAS_W = 800;
export const CANVAS_H = 300;
const GROUND_Y       = 236;   // canvas px
const HIGH_SCORE_KEY = 'pokeDashHighScore';
export const PLAYER_SPRITE = '/sprite.png'; // Togepi
export const BIRD_SPRITE = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/16.png'; // Pidgey

// ─── Game rules ───────────────────────────────────────────────────────────────
// The mechanics and numbers of Chrome's offline dino game (Chromium source, BSD licence).
// Everything here is in that game's own units: a 600-unit-wide world, speeds in units per frame at 60fps.
const WORLD_W        = 600;
const SCALE          = CANVAS_W / WORLD_W;
export const MS_PER_FRAME = 1000 / 60;
const START_SPEED    = 6;
const MAX_SPEED      = 13;
const ACCELERATION   = 0.001;   // speed gained per frame (about two minutes to reach top speed)
const GAP_COEFFICIENT     = 0.6;
const MAX_GAP_COEFFICIENT = 1.5;
const MAX_OBSTACLE_LENGTH = 3;  // balls can come in groups of up to 3
const MAX_OBSTACLE_DUPLICATION = 2;
const CLEAR_TIME          = 3000; // ms of open ground before the first obstacle
const GAMEOVER_CLEAR_TIME = 750;  // ms before a tap can restart, so you don't restart by accident
const SCORE_COEFFICIENT   = 0.025;
const GRAVITY             = 0.6;
const INITIAL_JUMP_VELOCITY = -10;
const DROP_VELOCITY       = -5;
const SPEED_DROP_COEFFICIENT = 3;
const MIN_JUMP_HEIGHT     = 30;   // rise where an early release turns into a short hop
const MAX_JUMP_HEIGHT     = 63;   // rise where a held jump levels off

export const PLAYER_X = 48;
export const PLAYER = { w: 46, h: 46, duckW: 54, duckH: 28 };
// Small and big Poké Balls stand in for the cacti, Pidgey for the pterodactyl
const OBSTACLE_TYPES = [
  { type: 'ball',    w: 26, h: 26, minGap: 120, multipleSpeed: 4,   minSpeed: 0 },
  { type: 'bigBall', w: 34, h: 34, minGap: 120, multipleSpeed: 7,   minSpeed: 0 },
  { type: 'bird',    w: 46, h: 40, minGap: 150, multipleSpeed: 999, minSpeed: 8.5, speedOffset: 0.8, rises: [0, 25, 50] },
];

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

export function freshState() {
  return {
    y: 0, vy: 0,                       // Togepi's rise above the ground, and its velocity (negative is up)
    jumping: false, reachedMin: false, released: false, speedDrop: false,
    ducking: false, duckHeld: false,
    obstacles: [], history: [],
    speed: START_SPEED, distance: 0, runTime: 0, groundOffset: 0,
    lastTime: null, lastScore: 0, overAt: 0,
  };
}

export function startJump(s) {
  if (s.jumping || s.ducking) return;
  s.jumping = true;
  s.vy = INITIAL_JUMP_VELOCITY - s.speed / 10;
  s.reachedMin = false;
  s.released = false;
  s.speedDrop = false;
}

export function endJump(s) {
  if (s.reachedMin && s.vy < DROP_VELOCITY) s.vy = DROP_VELOCITY;
}

function addObstacle(s) {
  // Any type that's fast enough to appear, and not the same type three times running
  const options = OBSTACLE_TYPES.filter(t => s.speed >= t.minSpeed
    && !(s.history.length >= MAX_OBSTACLE_DUPLICATION && s.history.every(h => h === t.type)));
  const t = options[randInt(0, options.length - 1)];
  let size = randInt(1, MAX_OBSTACLE_LENGTH);
  if (size > 1 && t.multipleSpeed > s.speed) size = 1;
  const w = t.w * size;
  // The gap is a distance: always enough to land and jump again at the current speed, plus some random extra
  const minGap = Math.round(w * s.speed + t.minGap * GAP_COEFFICIENT);
  const gap = randInt(minGap, Math.round(minGap * MAX_GAP_COEFFICIENT));
  s.obstacles.push({
    t, size, w, gap, x: WORLD_W, rot: 0, spawnedNext: false,
    rise: t.rises ? t.rises[randInt(0, t.rises.length - 1)] : 0,
    speedOffset: t.speedOffset ? (Math.random() > 0.5 ? t.speedOffset : -t.speedOffset) : 0,
  });
  s.history.unshift(t.type);
  s.history.length = Math.min(s.history.length, MAX_OBSTACLE_DUPLICATION);
}

// Hitboxes in world units: x from the left, y as rise above the ground
function playerBox(s) {
  return s.ducking
    ? { x: PLAYER_X + 6, y: s.y, w: PLAYER.duckW - 12, h: PLAYER.duckH - 6 }
    : { x: PLAYER_X + 9, y: s.y + 2, w: PLAYER.w - 18, h: PLAYER.h - 6 };
}

export function hits(s) {
  const p = playerBox(s);
  return s.obstacles.some((o) => {
    if (o.t.type === 'bird') {
      const b = { x: o.x + 4, y: o.rise + 6, w: o.w - 8, h: o.t.h - 14 };
      return p.x < b.x + b.w && p.x + p.w > b.x && p.y < b.y + b.h && p.y + p.h > b.y;
    }
    // Balls are round, so test each one as a circle
    for (let k = 0; k < o.size; k++) {
      const r = o.t.w / 2;
      const cx = o.x + k * o.t.w + r, cy = r;
      const nx = Math.max(p.x, Math.min(cx, p.x + p.w));
      const ny = Math.max(p.y, Math.min(cy, p.y + p.h));
      if ((cx - nx) ** 2 + (cy - ny) ** 2 < (r * 0.88) ** 2) return true;
    }
    return false;
  });
}

// One step of the game, f = how many 60fps frames have passed
export function step(s, dt) {
  const f = dt / MS_PER_FRAME;
  s.runTime += dt;
  s.speed = Math.min(MAX_SPEED, s.speed + ACCELERATION * f);
  s.distance += s.speed * f;
  s.groundOffset = (s.groundOffset + s.speed * f) % WORLD_W;

  if (s.jumping) {
    s.y -= (s.speedDrop ? s.vy * SPEED_DROP_COEFFICIENT : s.vy) * f;
    s.vy += GRAVITY * f;
    if (s.y > MIN_JUMP_HEIGHT || s.speedDrop) {
      s.reachedMin = true;
      if (s.released) endJump(s);
    }
    if (s.y > MAX_JUMP_HEIGHT || s.speedDrop) endJump(s);
    if (s.y <= 0) {
      s.y = 0; s.vy = 0;
      s.jumping = false; s.speedDrop = false;
      if (s.duckHeld) s.ducking = true;
    }
  }

  s.obstacles.forEach((o) => {
    const dx = (s.speed + o.speedOffset) * f;
    o.x -= dx;
    o.rot -= dx / (o.t.w / 2);
  });
  s.obstacles = s.obstacles.filter(o => o.x + o.w > 0);

  if (s.runTime > CLEAR_TIME) {
    const last = s.obstacles[s.obstacles.length - 1];
    if (!last) addObstacle(s);
    else if (!last.spawnedNext && last.x + last.w + last.gap < WORLD_W) {
      last.spawnedNext = true;
      addObstacle(s);
    }
  }
}

function drawBall(ctx, cx, cy, r, rot, big) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.arc(0, 0, r, Math.PI, 0);
  ctx.fillStyle = big ? '#2563eb' : '#ee1515'; // big ones are Great Balls
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  if (big) {
    ctx.fillStyle = '#ef4444';
    ctx.fillRect(-r * 0.62, -r * 0.82, r * 0.34, r * 0.5);
    ctx.fillRect(r * 0.28, -r * 0.82, r * 0.34, r * 0.5);
  }
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(1.5, r * 0.14);
  ctx.strokeStyle = '#18181b';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.lineTo(r, 0);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.lineWidth = Math.max(1.2, r * 0.1);
  ctx.stroke();
  ctx.restore();
}

// Draws one frame of the game. Shared by the game itself and the Arcade card's self-playing preview.
export function drawScene(ctx, s, sprites, now, running) {
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

  // Background
  const bg = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
  bg.addColorStop(0, '#0a0a0d');
  bg.addColorStop(1, '#050506');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // Ground line, with specks that scroll so you can feel the speed
  const groundGrad = ctx.createLinearGradient(0, 0, CANVAS_W, 0);
  groundGrad.addColorStop(0, '#04aff7');
  groundGrad.addColorStop(1, '#f42ae4');
  ctx.fillStyle = groundGrad;
  ctx.fillRect(0, GROUND_Y, CANVAS_W, 2);
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  ctx.fillRect(0, GROUND_Y + 2, CANVAS_W, CANVAS_H - GROUND_Y - 2);
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  for (let i = 0; i < 26; i++) {
    const x = (((i * 137) % WORLD_W) - s.groundOffset + WORLD_W) % WORLD_W;
    ctx.fillRect(x * SCALE, GROUND_Y + 8 + (i % 3) * 9, (i % 2 ? 3 : 7) * SCALE, 2);
  }

  // Togepi (squashed when ducking, with a little bounce while running)
  const w = (s.ducking ? PLAYER.duckW : PLAYER.w) * SCALE;
  const h = (s.ducking ? PLAYER.duckH : PLAYER.h) * SCALE;
  const bob = running && !s.jumping && !s.ducking ? Math.abs(Math.sin(s.distance * 0.12)) * 2 : 0;
  const px = PLAYER_X * SCALE;
  const py = GROUND_Y - s.y * SCALE - h - bob;
  if (sprites.player) {
    ctx.drawImage(sprites.player, px, py, w, h);
  } else {
    ctx.fillStyle = '#FCD34D';
    ctx.beginPath();
    ctx.ellipse(px + w / 2, py + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Obstacles
  s.obstacles.forEach((o) => {
    if (o.t.type === 'bird') {
      const flap = Math.sin(now / 90) * 3 * SCALE;
      const cx = (o.x + o.w / 2) * SCALE;
      const cy = GROUND_Y - (o.rise + o.t.h / 2) * SCALE + flap;
      if (sprites.bird) {
        const size = 100 * SCALE; // the sprite has empty space around Pidgey
        ctx.drawImage(sprites.bird, cx - size / 2, cy - size / 2, size, size);
      } else {
        ctx.fillStyle = '#a16207';
        ctx.beginPath();
        ctx.ellipse(cx, cy, (o.w / 2) * SCALE, (o.t.h / 2.5) * SCALE, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
    const r = (o.t.w / 2) * SCALE;
    for (let k = 0; k < o.size; k++) {
      drawBall(ctx, (o.x + k * o.t.w) * SCALE + r, GROUND_Y - r, r, o.rot, o.t.type === 'bigBall');
    }
  });
}

// Where the scores live. On the site it's Firebase's live connection; the Discord Activity passes its own with the
// same three calls, as that connection can't get through Discord's proxy
export const firebaseScores = (database) => ({
  watch: (onScores) => onValue(ref(database, 'pokeDashLeaderboard'), (snap) => onScores(snap.val() || {})),
  get: (uid) => get(ref(database, `pokeDashLeaderboard/${uid}`)).then((snap) => snap.val()),
  save: (uid, entry) => set(ref(database, `pokeDashLeaderboard/${uid}`), entry),
});

export default function PokeDash({ database, scores: scoresProp, discordUser, onLogin, onGameOver, birdSprite = BIRD_SPRITE }) {
  const onGameOverRef = useRef(onGameOver);  // told each run's score (the Activity passes it on to the bot)
  onGameOverRef.current = onGameOver;
  const canvasRef = useRef(null);
  const spriteRef = useRef(null);
  const birdRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef(freshState());
  const gameStateRef = useRef('idle');
  const [gameState, setGameStateRaw] = useState('idle'); // idle | playing | over
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [flashKey, setFlashKey] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const myUid = discordUser?.id;
  const scores = useMemo(() => scoresProp || (database ? firebaseScores(database) : null), [scoresProp, database]);

  const setGameState = useCallback((v) => { gameStateRef.current = v; setGameStateRaw(v); }, []);

  useEffect(() => {
    if (!scores) return;
    return scores.watch((data) => {
      const list = Object.values(data)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
      setLeaderboard(list);
    });
  }, [scores]);

  useEffect(() => {
    try {
      const saved = parseInt(localStorage.getItem(HIGH_SCORE_KEY), 10);
      if (!isNaN(saved)) setHighScore(saved);
    } catch (e) { /* storage blocked */ }
  }, []);

  useEffect(() => {
    const load = (src, target) => {
      const img = new Image();
      img.onload = () => { target.current = img; };
      img.src = src;
    };
    load(PLAYER_SPRITE, spriteRef);
    load(birdSprite, birdRef);
  }, [birdSprite]);

  const startGame = useCallback(() => {
    stateRef.current = freshState();
    setScore(0);
    setGameState('playing');
  }, [setGameState]);

  // ─── Controls ───
  const pressJump = useCallback(() => {
    const s = stateRef.current;
    const gs = gameStateRef.current;
    if (gs === 'idle') startGame();
    else if (gs === 'over') { if (performance.now() - s.overAt > GAMEOVER_CLEAR_TIME) startGame(); }
    else startJump(s);
  }, [startGame]);

  const releaseJump = useCallback(() => {
    const s = stateRef.current;
    s.released = true;
    endJump(s);
  }, []);

  const pressDuck = useCallback(() => {
    const s = stateRef.current;
    if (gameStateRef.current !== 'playing') return;
    s.duckHeld = true;
    if (s.jumping) { s.speedDrop = true; s.vy = 1; } // in the air: drop down fast
    else s.ducking = true;
  }, []);

  const releaseDuck = useCallback(() => {
    const s = stateRef.current;
    s.duckHeld = false;
    s.ducking = false;
    s.speedDrop = false;
  }, []);

  const draw = useCallback((ctx, now) => drawScene(
    ctx, stateRef.current, { player: spriteRef.current, bird: birdRef.current }, now, gameStateRef.current === 'playing'), []);

  // ─── Game loop ───
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const tick = (now) => {
      const s = stateRef.current;
      if (gameStateRef.current === 'playing') {
        if (s.lastTime == null) s.lastTime = now;
        const dt = Math.min(now - s.lastTime, 50); // don't jump ahead after a pause or a hidden tab
        s.lastTime = now;
        step(s, dt);

        const newScore = Math.floor(s.distance * SCORE_COEFFICIENT);
        if (newScore !== s.lastScore) {
          if (Math.floor(newScore / 100) > Math.floor(s.lastScore / 100)) setFlashKey(k => k + 1); // flash every 100
          s.lastScore = newScore;
          setScore(newScore);
        }

        if (hits(s)) {
          s.overAt = now;
          setGameState('over');
          onGameOverRef.current?.(newScore);
          setHighScore((prevHigh) => {
            if (newScore > prevHigh) {
              try { localStorage.setItem(HIGH_SCORE_KEY, String(newScore)); } catch (e) { /* storage blocked */ }
              return newScore;
            }
            return prevHigh;
          });
          if (scores && myUid && newScore > 0) {
            scores.get(myUid).then((existing) => {
              if (!existing || newScore > existing.score) {
                scores.save(myUid, {
                  uid: myUid,
                  username: discordUser.nickname || discordUser.username,
                  avatar: discordUser.avatar ? `https://cdn.discordapp.com/avatars/${myUid}/${discordUser.avatar}.png` : null,
                  score: newScore,
                });
              }
            });
          }
        }
      } else {
        s.lastTime = null;
      }

      draw(ctx, now);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [draw, discordUser, scores, myUid, setGameState]);

  useEffect(() => {
    const down = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        if (!e.repeat) pressJump();
      } else if (e.code === 'ArrowDown') {
        e.preventDefault();
        if (!e.repeat) pressDuck();
      }
    };
    const up = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') releaseJump();
      else if (e.code === 'ArrowDown') releaseDuck();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [pressJump, releaseJump, pressDuck, releaseDuck]);

  // Hold-to-press handlers for the game area and the on-screen buttons
  const holdHandlers = (press, release) => ({
    onMouseDown: press,
    onMouseUp: release,
    onMouseLeave: release,
    onTouchStart: (e) => { e.preventDefault(); press(e); },
    onTouchEnd: (e) => { e.preventDefault(); release(e); },
    onTouchCancel: release,
  });

  // ─── RENDER: Not logged in ─────────────────────────────────────────────────
  if (!discordUser) {
    return (
      <div className="relative flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <div
          className="fixed left-0 right-0 bottom-0 pointer-events-none"
          style={{ top: '100px', background: 'linear-gradient(160deg, rgba(4,175,247,0.08), rgba(0,0,0,0.4))' }}
        />
        <img src="/pball.svg" alt="" className="relative z-10 h-12 w-12 animate-spin" style={{ animationDuration: '2s', filter: 'brightness(0) invert(1)' }} />
        <div className="relative z-10">
          <h2 className="text-white font-bold text-xl">Cheeky Dash</h2>
          <p className="text-white text-sm mt-1">Get Togepi past the obstacles — how far can you get?</p>
        </div>
        <div className="relative z-10 flex flex-col gap-1 rounded-xl px-4 py-3 border border-zinc-800/40 backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <p className="text-white text-sm">You need to log in with Discord to play!</p>
          <button
            onClick={onLogin}
            className="flex items-center justify-center gap-1.5 text-white font-semibold text-xs rounded-lg px-4 py-2 transition-all duration-200 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #04aff7, #f42ae4)' }}
          >
            <LogIn className="h-3.5 w-3.5 flex-shrink-0" />
            Log in with Discord
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDER: Logged in — the game ──────────────────────────────────────────
  const pad = (n) => String(n).padStart(5, '0');
  return (
    <div className="flex-1 flex flex-col items-center px-4 py-6 gap-3">
      <style>{`
        @keyframes scoreFlash { 0%, 100% { opacity: 1; } 16%, 50%, 83% { opacity: 0; } 33%, 66% { opacity: 1; } }
        .score-flash { animation: scoreFlash 0.75s steps(1) both; }
      `}</style>
      {leaderboard.length > 0 && (
        <div className="w-full rounded-xl border border-zinc-800/60 corner-glow bg-gradient-to-br from-zinc-800/85 to-black/90 px-3 py-2.5" style={{ maxWidth: CANVAS_W }}>
          <style>{`
            @keyframes trophyBounce { 0%, 100% { transform: translateY(0) rotate(0deg); } 25% { transform: translateY(-2px) rotate(-8deg); } 50% { transform: translateY(0) rotate(0deg); } 75% { transform: translateY(-2px) rotate(8deg); } }
            .trophy-bounce { display: inline-block; animation: trophyBounce 1.4s ease-in-out infinite; }
          `}</style>
          <div className="flex items-center gap-1.5">
            <span className="trophy-bounce" style={{ fontSize: '12px' }}>🏆</span>
            <p className="text-white text-[10px] font-bold uppercase tracking-wider">Leaderboard</p>
            <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">Top {leaderboard.length}</p>
          </div>
          <div className="flex flex-col gap-1 mt-1.5">
            {leaderboard.map((entry, i) => (
              <div
                key={entry.uid}
                className="flex items-center gap-2"
                style={entry.uid === myUid ? { background: 'rgba(4,175,247,0.12)', borderRadius: '6px', padding: '2px 4px', margin: '-2px -4px' } : undefined}
              >
                <span className="text-zinc-400 font-mono text-[10px] w-4 flex-shrink-0 text-center">{i + 1}</span>
                <span className="text-white text-[10px] font-medium flex-1 truncate">{entry.username}</span>
                <span className="text-white text-[10px] font-semibold font-mono flex-shrink-0">{entry.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center gap-3 w-full">
      <div className="flex items-center justify-between w-full" style={{ maxWidth: CANVAS_W }}>
        <h2 className="text-white font-bold text-base">Cheeky Dash</h2>
        <div className="flex items-center gap-3 text-xs font-mono">
          {highScore > 0 && <span className="text-zinc-500">HI <span className="text-zinc-300 font-semibold">{pad(highScore)}</span></span>}
          <span key={flashKey} className={flashKey > 0 ? 'score-flash text-zinc-400' : 'text-zinc-400'}>
            SCORE <span className="text-white font-semibold">{pad(score)}</span>
          </span>
        </div>
      </div>

      <div
        className="relative w-full rounded-xl overflow-hidden border border-zinc-800/60 corner-glow select-none"
        style={{ maxWidth: CANVAS_W, aspectRatio: `${CANVAS_W} / ${CANVAS_H}`, cursor: 'pointer', touchAction: 'none' }}
        {...holdHandlers(pressJump, releaseJump)}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="w-full h-full block"
          style={{ imageRendering: 'pixelated' }}
        />

        {gameState !== 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none" style={{ background: 'rgba(0,0,0,0.45)' }}>
            {gameState === 'idle' && (
              <>
                <p className="text-white font-bold text-lg">Tap anywhere to start</p>
                <p className="text-zinc-300 text-xs">or press Space</p>
              </>
            )}
            {gameState === 'over' && (
              <>
                <p className="text-white font-bold text-lg">Game Over</p>
                <p className="text-zinc-300 text-xs">Score: {score}{score >= highScore && score > 0 ? ' — New High Score!' : ''}</p>
                <p className="text-zinc-400 text-[11px] mt-1">Tap anywhere to try again</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Tap area: everything below the game jumps (hold for a higher jump). The Duck button sits inside it. */}
      <div
        className="flex-1 w-full flex flex-col items-center justify-between gap-4 rounded-xl select-none cursor-pointer pt-3 pb-2"
        style={{ maxWidth: CANVAS_W, minHeight: '260px', touchAction: 'none', WebkitTapHighlightColor: 'transparent' }}
        {...holdHandlers(pressJump, releaseJump)}
      >
        <p className="text-zinc-500 text-[11px] text-center pointer-events-none">
          {gameState === 'playing'
            ? 'Tap anywhere here to jump. Hold for a higher jump.'
            : 'Tap the game or anywhere here to start'}
        </p>
        <div className="flex flex-col items-center gap-1.5">
          <button
            {...holdHandlers(
              (e) => { e?.stopPropagation?.(); pressDuck(); },
              (e) => { e?.stopPropagation?.(); releaseDuck(); },
            )}
            className="flex items-center justify-center font-black text-white text-xs uppercase tracking-wide active:scale-90 transition-transform duration-100 select-none"
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #04aff7, #0284c7)',
              boxShadow: '0 6px 18px rgba(4,175,247,0.45)',
              touchAction: 'none',
            }}
          >
            Duck
          </button>
          <p className="text-zinc-600 text-[10px] text-center pointer-events-none">Duck under Pidgey, or drop fast mid-jump (↓ on keyboard)</p>
        </div>
      </div>
      </div>
    </div>
  );
}
